import { ScrollReveal, SplitReveal } from '@/components/motion';
import { BRAND_PROMISE, CORE_VALUES, DELIVERY_FRAMEWORK, FOUNDATIONAL_COMMITMENT, VISION_STATEMENT } from '@/data/about';

/**
 * VisionMission (/about — section 1) — "Our vision & mission".
 *
 * Opens with the eyebrow + SplitReveal headline, then features the verbatim
 * VISION_STATEMENT as a large display quote. A "Mission & commitment" block
 * follows, presenting the FOUNDATIONAL_COMMITMENT tagline and BRAND_PROMISE
 * as the guiding statements in place of a formal mission statement (none is
 * published on the source site — none is fabricated here). CORE_VALUES render
 * as a row of gold-bordered pill chips, and the FLEXI/EDU/CARE DELIVERY_FRAMEWORK
 * renders as three plain hover-lift cards. Every institutional fact comes from
 * '@/data/about'; only connective microcopy is authored here.
 *
 * Motion: SplitReveal headline, ScrollReveal for the quote, commitment block,
 * value chips and framework cards (staggered where they are a list). All
 * primitives are reduced-motion safe and never strand content hidden.
 */
export function VisionMission() {
  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            About IBAS · Vision &amp; mission
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Our vision &amp; mission.
          </SplitReveal>
        </header>

        {/* ── Vision statement — large display quote ────────────────────── */}
        <ScrollReveal from="up" delay={0.1}>
          <blockquote className="mt-10 border-l-2 border-brand-gold pl-6 md:pl-8">
            <p className="font-display text-2xl leading-snug text-foreground md:text-3xl">
              &ldquo;{VISION_STATEMENT}&rdquo;
            </p>
          </blockquote>
        </ScrollReveal>

        {/* ── Mission & commitment ──────────────────────────────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Mission &amp; commitment
          </p>
          <ScrollReveal
            stagger={0.1}
            className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2"
          >
            <div className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0">
              <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                Our guiding commitment
              </p>
              <p className="mt-3 font-display text-xl leading-snug font-semibold text-foreground">
                {FOUNDATIONAL_COMMITMENT}
              </p>
            </div>
            <div className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0">
              <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                Our promise
              </p>
              <p className="mt-3 font-display text-xl leading-snug font-semibold text-foreground">
                {BRAND_PROMISE}
              </p>
            </div>
          </ScrollReveal>
        </div>

        {/* ── Core values — pill row ─────────────────────────────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Our core values
          </p>
          <ScrollReveal stagger={0.05} className="mt-6 flex flex-wrap gap-3">
            {CORE_VALUES.map((value) => (
              <span
                key={value}
                className="rounded-full border border-brand-gold/30 bg-muted/30 px-5 py-2 font-label text-sm text-brand-navy"
              >
                {value}
              </span>
            ))}
          </ScrollReveal>
        </div>

        {/* ── Delivery framework — FLEXI · EDU · CARE ───────────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            How we deliver
          </p>
          <ScrollReveal
            stagger={0.1}
            className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3"
          >
            {DELIVERY_FRAMEWORK.map((pillar) => (
              <div
                key={pillar.key}
                className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <div className="flex size-12 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                  <pillar.Icon aria-hidden className="size-6" />
                </div>
                <h3 className="mt-5 font-display text-xl leading-tight font-semibold text-foreground">
                  {pillar.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {pillar.description}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
